import React from 'react';
import { Dialog, Box, Typography } from '@mui/material';
import AccidentalText from './AccidentalText';
import { SELECTED_BG, SELECTED_FG, SELECTED_BG_HOVER } from '../config/uiTheme';

// Chip d'une gamme proposee. Le blanc marque celle qui est posee sur le manche,
// exactement comme le verrou de gamme du mode jam. Le texte ambre signale une
// gamme harsh : elle couvre la progression mais frotte un demi-ton au-dessus
// d'une note jouee, meme code couleur que le badge des accords dim.
const ScaleChip = ({ label, active, dimmed, harsh, title, clickable = !active, onClick }) => (
  <Box
    onClick={clickable ? onClick : undefined}
    title={title}
    sx={{
      px: 1.5,
      py: 0.5,
      borderRadius: '8px',
      cursor: clickable ? 'pointer' : 'default',
      opacity: dimmed && !active ? 0.75 : 1,
      backgroundColor: active ? SELECTED_BG : 'rgba(255,255,255,0.08)',
      transition: 'all 0.15s ease',
      ...(clickable && {
        '&:hover': {
          backgroundColor: active ? SELECTED_BG_HOVER : 'rgba(255,255,255,0.14)',
        },
      }),
    }}
  >
    <Typography sx={{
      fontSize: '0.85rem',
      color: active ? SELECTED_FG : harsh ? 'rgba(245,158,11,0.9)' : 'rgba(255,255,255,0.8)',
      fontWeight: active ? 600 : 'normal',
    }}>
      <AccidentalText text={label} />
    </Typography>
  </Box>
);

/**
 * Gammes posables sur la progression en cours. Choisir une gamme ne change que
 * ce qui s'allume sur le manche : les accords continuent de sonner sur la gamme
 * harmonique, qui reste en tete de liste comme retour. En backing, un choix en
 * pause sur la section n'est pas dans la liste : la base reste cliquable pour
 * le retirer.
 */
const ImproScalesDialog = ({
  open,
  onClose,
  baseLabel,
  progressionLabel,
  scales = [],
  improScale,
  pausedLabel = null,
  onPick,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        bgcolor: '#15151A',
        backgroundImage: 'none',
        borderRadius: '14px',
        border: '1px solid rgba(255,255,255,0.08)',
        maxWidth: 340,
        mx: 2,
      },
    }}
  >
    <Box sx={{ p: 2.5 }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'white', mb: 0.5 }}>
        Compatible Scales
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', mb: pausedLabel ? 0.5 : 2 }}>
        {progressionLabel
          ? `Play over ${progressionLabel} - the chords stay on ${baseLabel}`
          : `The chords stay on ${baseLabel}`}
      </Typography>
      {pausedLabel && (
        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', mb: 2 }}>
          <AccidentalText text={`${pausedLabel} paused on this section`} />
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <ScaleChip
          label={baseLabel}
          active={!improScale}
          clickable={!!improScale || !!pausedLabel}
          title={pausedLabel ? `Drop ${pausedLabel} and follow the track` : 'The scale the chords are built from'}
          onClick={() => { onPick(null); onClose(); }}
        />
        {scales.map((scale) => (
          <ScaleChip
            key={`${scale.root}|${scale.mode}`}
            label={scale.label}
            active={!!improScale && improScale.root === scale.root && improScale.mode === scale.mode}
            dimmed={!scale.covers}
            harsh={scale.harsh}
            title={scale.harsh
              ? 'Covers every chord, but rubs a half step above a chord tone - spicy'
              : scale.covers
                ? 'Covers every chord of the progression'
                : 'Stays inside the key, but does not cover every chord tone'}
            onClick={() => { onPick({ root: scale.root, mode: scale.mode }); onClose(); }}
          />
        ))}
      </Box>
      {scales.length === 0 && (
        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', mt: 1 }}>
          No other scale fits this progression.
        </Typography>
      )}
    </Box>
  </Dialog>
);

export default ImproScalesDialog;
